import { Feature } from 'src/replicant/analytics';

export type AnalyticsEvent<T extends string = string> = {
  /** Defaults to current user ID. */
  userId?: string;
  eventType: T;
  eventProperties: Record<string, unknown>;
  userProperties?: Record<string, unknown>;
};

/**
 * Currency grant event
 *
 * @example
 *
 * const events = [];
 *
 *  for (const id of ids) {
 *     const now = api.date.now();
 *     const oldCurrencies = Inventory.getters.getCurrencies(state, now);
 *
 *     // Purchase
 *     purchaseItem(state, id, now);
 *
 *     // Create analytics event
 *     events.push(
 *      getCurrencyGrantEvent(
 *         {
 *           feature: 'decoration',
 *           $subFeature: id,
 *         },
 *         {
 *           old: oldCurrencies,
 *           new: Inventory.getters.getCurrencies(state, now),
 *         },
 *       ),
 *    );
 *   }
 *
 *    // Send CurrencyGrant events
 *    sendAnalyticsEvents(api, events);
 */
export type CurrencyGrantFeature =
  | {
      feature:
        | 'match2'
        | 'match2_team'
        | 'match2_leagues'
        | 'match_race'
        | 'decoration_progression'
        | 'random_box_spin'
        | 'debut'
        | 'visit'
        | 'tutorial_reward'
        | 'currency_exchange'
        | 'minigame_reward'
        | 'gift'
        | 'catalog_sell'
        | 'frenzy'
        | 'promo'
        | 'match2_tournament'
        | 'tutorial_reset_energy'
        | 'magic_lamps'
        | 'marketing'
        | 'resubscribe'
        | 'purchase'
        | 'gems_product'
        | 'airplane_tournament'
        | 'cards'
        | 'weekly_leaderboard'
        | 'treasure_express'
        | 'rescue_level'
        | 'baking_buddies'
        | 'janken'
        | 'dream_drift';
      $subFeature: string;
    }
  | {
      feature:
        | 'progression'
        | 'invite_reward'
        | 'energy_refill'
        | 'player_level_up'
        | 'friend_visit_reward'
        | 'daily_bonus'
        | 'match_level_completed'
        | 'match_frenzy_rewards_claimed'
        | 'match_battle_pass_rewards_claimed'
        | 'match_daily_challenge_rewards_claimed'
        | 'streak_battle_royale'
        | 'admin_message'
        | 'native_install'
        | 'native_incentivised_update'
        | 'daily_challenge'
        | 'resurrection'
        | 'message'
        | 'test'
        | 'guest_to_line'
        | 'resurrect'
        | 'ftue_landing';
    }
  | {
      feature: 'event_reward';
      $subFeature: string;
      event_id: string;
    }
  | {
      feature: typeof Feature.BUMBUM._;
      $subFeature: typeof Feature.BUMBUM.LEADERBOARD_REWARD;
      competition_id: string;
    }
  | {
      feature: typeof Feature.FRENZY._;
      $subFeature: typeof Feature.FRENZY.LEADERBOARD_REWARD;
      eventID: string;
    }
  | {
      feature: typeof Feature.HOME_SHORTCUT._;
      $subFeature:
        | typeof Feature.HOME_SHORTCUT.ADD
        | typeof Feature.HOME_SHORTCUT.LAUNCH;
    };

export type CurrencyConsumeFeature =
  | { feature: 'pet_level_up' | 'cheat' | 'admin_message' | 'message' | 'test' }
  | {
      feature:
        | 'visit'
        | 'match2_team'
        | 'redo_evolution'
        | 'decoration_progression'
        | 'growth_capsule'
        | 'accessories'
        | 'debut'
        | 'care_action'
        | 'currency_exchange'
        | 'minigame_play'
        | 'decoration'
        | 'daycare'
        | 'gacha'
        | 'catalog'
        | 'catalog_fusion'
        | 'puzzle'
        | 'match_level_failed'
        | 'match_continue'
        | 'buy_power_blocks'
        | 'buy_boosters'
        | 'magic_lamps'
        | 'match2'
        | 'airplane_tournament'
        | 'super_lollipop';
      $subFeature: string;
    }
  | {
      feature: 'event_minigame_play';
      $subFeature: string;
      event_id: string;
    }
  | {
      feature: 'random_box_spin';
      $subFeature: string;
      isAuto: boolean;
    };

export type Currencies = {
  points: number;
  energy: number;
  freeGems: number;
  paidGems: number;
};

export type EventCurrencies = {
  energy: number;
  gachaTickets: number;
};

type RandomBoxCurrencies =
  | {
      energy: number;
    }
  | {
      energy: number;
      frenzyPoints: number;
    };

type Match2ProgressionCurrencies = {
  stars: number;
};

/** Dream Drift identity added to milestone currency grant events. */
export type DreamDriftCurrencyGrantProperties = {
  /** Server timestamp identifying the rewarded Dream Drift instance. */
  instanceId: number;
  /** Theme assigned to the rewarded Dream Drift instance. */
  themeId: string;
};

export type CurrencyAnalytics<
  TCurrencies extends
    | Currencies
    | EventCurrencies
    | RandomBoxCurrencies
    | Match2ProgressionCurrencies,
> = {
  old: TCurrencies;
  new: TCurrencies;
};

export function getCurrencyGrantEventProperties(
  eventCurrencies: CurrencyAnalytics<EventCurrencies>,
) {
  const { energy: oldEEnergy, gachaTickets: oldEGachaTickets } =
    eventCurrencies.old;
  const { energy: newEEnergy, gachaTickets: newEGachaTickets } =
    eventCurrencies.new;

  return {
    oldEEnergy,
    newEEnergy,
    oldEGachaTickets,
    newEGachaTickets,
    eEnergyGranted: newEEnergy - oldEEnergy,
    eGachaTicketsGranted: newEGachaTickets - oldEGachaTickets,
  };
}

export function getCurrencyConsumeEventProperties(
  eventCurrencies: CurrencyAnalytics<EventCurrencies>,
) {
  const { energy: oldEEnergy, gachaTickets: oldEGachaTickets } =
    eventCurrencies.old;
  const { energy: newEEnergy, gachaTickets: newEGachaTickets } =
    eventCurrencies.new;

  return {
    oldEEnergy,
    newEEnergy,
    oldEGachaTickets,
    newEGachaTickets,
    eEnergyConsumed: oldEEnergy - newEEnergy,
    eGachaTicketsConsumed: oldEGachaTickets - newEGachaTickets,
  };
}

type RandomBoxExtraProperties = {
  oldRbEnergy: number;
  newRbEnergy: number;
  rbEnergyGranted: number;
};

export function getCurrencyGrantRandomBoxProperties(
  currencies: CurrencyAnalytics<RandomBoxCurrencies>,
): RandomBoxExtraProperties {
  const { energy: oldRbEnergy } = currencies.old;
  const { energy: newRbEnergy } = currencies.new;

  return {
    oldRbEnergy,
    newRbEnergy,
    rbEnergyGranted: newRbEnergy - oldRbEnergy,
  };
}

export function getCurrencyConsumeRandomBoxProperties(
  currencies: CurrencyAnalytics<RandomBoxCurrencies>,
) {
  const { energy: oldRbEnergy } = currencies.old;
  const { energy: newRbEnergy } = currencies.new;

  return {
    oldRbEnergy,
    newRbEnergy,
    rbEnergyConsumed: oldRbEnergy - newRbEnergy,
  };
}

export function getCurrencyGrantMatch2ProgressionProperties(
  currencies: CurrencyAnalytics<Match2ProgressionCurrencies>,
) {
  const oldStars = currencies.old.stars;
  const newStars = currencies.new.stars;

  return {
    oldStars,
    newStars,
    starsGranted: newStars - oldStars,
  };
}

export function getCurrencyConsumeMatch2ProgressionProperties(
  currencies: CurrencyAnalytics<Match2ProgressionCurrencies>,
) {
  const oldStars = currencies.old.stars;
  const newStars = currencies.new.stars;

  return {
    oldStars,
    newStars,
    starsConsumed: oldStars - newStars,
  };
}

// Type safety here mostly because of the refactor
export type AdditionalCurrencyGrantProperties =
  | ReturnType<typeof getCurrencyGrantEventProperties>
  | ReturnType<typeof getCurrencyGrantRandomBoxProperties>
  | ReturnType<typeof getCurrencyGrantMatch2ProgressionProperties>
  | DreamDriftCurrencyGrantProperties;

export type AdditionalCurrencyConsumeProperties =
  | ReturnType<typeof getCurrencyConsumeEventProperties>
  | ReturnType<typeof getCurrencyConsumeRandomBoxProperties>
  | ReturnType<typeof getCurrencyConsumeMatch2ProgressionProperties>;

export type CurrencyGrantEvent = ReturnType<typeof getCurrencyGrantEvent>;

export const getCurrencyGrantEvent = (
  type: CurrencyGrantFeature,
  currencies: CurrencyAnalytics<Currencies>,
  now: number,
  // Catch all for currencies beyond the default ones
  additionalProperties: AdditionalCurrencyGrantProperties = {} as any,
  // For random box games, only the rb energy is relevant.
  // It's already included in the additional properties.
  // Excluding the regular energy is the path of least resistance here.
  excludeEnergy = false,
  // The purhcaseID for currency that came from a purchase.
  purchaseID: string | undefined = undefined,
) => {
  const {
    points: oldBPoints,
    energy: oldEnergy,
    freeGems: oldFreeGems,
    paidGems: oldPaidGems,
  } = currencies.old;
  const oldTotalGems = oldFreeGems + oldPaidGems;
  const {
    points: newBPoints,
    energy: newEnergy,
    freeGems: newFreeGems,
    paidGems: newPaidGems,
  } = currencies.new;
  const newTotalGems = newFreeGems + newPaidGems;

  const energyProperties = excludeEnergy
    ? {}
    : {
        oldEnergy,
        newEnergy,
        energyGranted: newEnergy - oldEnergy,
      };

  return {
    eventType: 'CurrencyGrant',
    eventProperties: {
      ...type,
      oldFreeGems,
      newFreeGems,
      oldPaidGems,
      newPaidGems,
      oldTotalGems,
      newTotalGems,
      freeGemsGranted: newFreeGems - oldFreeGems,
      paidGemsGranted: newPaidGems - oldPaidGems,
      totalGemsGranted: newTotalGems - oldTotalGems,
      oldBPoints,
      newBPoints,
      bPointsGranted: newBPoints - oldBPoints,
      timestamp: new Date(now).toISOString(),
      ...energyProperties,
      ...additionalProperties,
      purchaseID,
    },
  } as const;
};

export type CurrencyConsumeEvent = ReturnType<typeof getCurrencyConsumeEvent>;

export const getCurrencyConsumeEvent = (
  type: CurrencyConsumeFeature,
  currencies: CurrencyAnalytics<Currencies>,
  now: number,
  // Catch all for currencies beyond the default ones
  additionalProperties: AdditionalCurrencyConsumeProperties = {} as any,
  // For random box games, only the rb energy is relevant.
  // It's already included in the additional properties.
  // Excluding the regular energy is the path of least resistance here.
  excludeEnergy = false,
) => {
  const {
    points: oldBPoints,
    energy: oldEnergy,
    freeGems: oldFreeGems,
    paidGems: oldPaidGems,
  } = currencies.old;
  const oldTotalGems = oldFreeGems + oldPaidGems;
  const {
    points: newBPoints,
    energy: newEnergy,
    freeGems: newFreeGems,
    paidGems: newPaidGems,
  } = currencies.new;
  const newTotalGems = newFreeGems + newPaidGems;

  const energyProperties = excludeEnergy
    ? {}
    : {
        oldEnergy,
        newEnergy,
        energyConsumed: oldEnergy - newEnergy,
      };

  return {
    eventType: 'CurrencyConsume',
    eventProperties: {
      ...type,
      oldFreeGems,
      newFreeGems,
      oldPaidGems,
      newPaidGems,
      oldTotalGems,
      newTotalGems,
      oldBPoints,
      newBPoints,
      freeGemsConsumed: oldFreeGems - newFreeGems,
      paidGemsConsumed: oldPaidGems - newPaidGems,
      totalGemsConsumed: oldTotalGems - newTotalGems,
      bPointsConsumed: oldBPoints - newBPoints,
      timestamp: new Date(now).toISOString(),
      ...energyProperties,
      ...additionalProperties,
      ...(type.feature === 'random_box_spin' ? { isAuto: type.isAuto } : {}),
    },
  } as const;
};
